import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { ApiError } from "@/lib/api";
import { useDashboard } from "./api";
import { DelinquentBanner } from "./DelinquentBanner";

// Phase 4a customer-portal — top-level shell. Includes the global
// header (FR-18 — logo + Portal label + avatar/menu mobile-collapsed
// to logo + avatar) and the delinquency banner (AC-9, stub-driven in
// v1). Children render via <Outlet />.
//
// Auth gate (FR-1 / AC-1) — subscribes to the React-Query cache for
// **any** portal query failing with 401, not just the dashboard. The
// dashboard query is still rendered for the delinquency banner, but
// the redirect itself is cache-driven so deep links like
// `/portal/orders/X` bounce too when the session expires there.
export function PortalLayout(): JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const dashboard = useDashboard();

  useEffect(() => {
    const cache = queryClient.getQueryCache();
    const unsubscribe = cache.subscribe((event) => {
      // Only react to the specific query that just transitioned —
      // scanning the whole cache would also match stale 401s left
      // over from a previous (now signed-back-in) session, causing
      // an unwanted bounce on the next cache event.
      if (event?.type !== "updated") return;
      const query = event.query;
      const key = query.queryKey;
      if (!Array.isArray(key) || key[0] !== "portal") return;
      const err = query.state.error;
      if (!(err instanceof ApiError) || err.status !== 401) return;
      // Only when the query is idle (fetch settled) — react-query
      // emits "updated" with the previous error still set while a
      // refetch is in flight; ignoring those avoids double-bounce.
      if (query.state.fetchStatus !== "idle") return;

      // Clear stale portal queries so a return visit fetches fresh
      // and doesn't trip this handler again with the cached 401.
      queryClient.removeQueries({ queryKey: ["portal"] });
      const back = encodeURIComponent(location.pathname + location.search);
      navigate(`/sign-in?return_to=${back}`, { replace: true });
    });
    return unsubscribe;
  }, [queryClient, location.pathname, location.search, navigate]);

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-6 px-4 py-6 md:px-6 md:py-8">
      <PortalHeader />
      {dashboard.data?.delinquent ? <DelinquentBanner /> : null}
      <Outlet />
    </div>
  );
}

// Phase 4j portal-polish — slimmed from the original card to a thin
// sub-nav strip. The previous "card with shadow" treatment took ~80px
// to display a wordmark + two links; the new strip is ~40px and frees
// the vertical real estate for the KPI strip on the dashboard.
//
// Visual vocabulary preserved: italic serif JDR wordmark, flag-navy
// "Portal" eyebrow, brand-red "My account" link. The audit's
// brand-red top hairline lived on the old card chrome; on a thin
// strip it reads as visual noise rather than identity.
function PortalHeader(): JSX.Element {
  return (
    <header className="flex items-center justify-between gap-4 border-b border-zinc-200 pb-3">
      <Link to="/" aria-label="JDR Equipment Rentals — home" className="inline-flex items-center gap-2">
        <img
          src="/brand/jdr-monogram-180.png"
          alt=""
          aria-hidden="true"
          width={28}
          height={28}
          className="h-7 w-7"
        />
        <span className="font-serif text-sm font-bold italic tracking-tight text-brand-black">
          JDR
        </span>
        <span className="hidden text-[10px] font-semibold uppercase tracking-[0.2em] text-flag-navy sm:inline">
          Portal
        </span>
      </Link>
      <nav aria-label="Portal" className="flex items-center gap-4 text-sm">
        <Link to="/portal" className="text-zinc-600 hover:text-brand-black">
          Dashboard
        </Link>
        <Link
          to="/portal/profile"
          className="font-semibold text-brand-red transition hover:text-brand-red-dark hover:underline"
        >
          My account
        </Link>
      </nav>
    </header>
  );
}
